import { useCallback, useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { isAdmin, useIdentity } from '@/entities/session';
import { GuideLauncher } from '@/features/guide';
import { LogoutButton } from '@/features/auth';
import { NotificationBell } from '@/features/notifications';
import { SCREEN_PATHS } from '@/shared/routes';

import { navIcon } from './navIcons';
import { ThemeToggle } from './ThemeToggle';
import { UserChip } from './UserChip';
import {
  DEFAULT_EXPANDED,
  NAV_COLLAPSED_KEY,
  NAV_GROUPS,
  NAV_SEEN_KEY,
  NAV_STORAGE_KEY,
} from './navigation';

/* The application frame: a brand corner, a top bar, a grouped sidebar, and the page.
 *
 * **Why grouped and collapsible.** The sidebar this replaces held twenty-eight links under three
 * headings, seventeen of them under one — a list read top to bottom on every visit. Seven groups of
 * four to seven entries is scannable, and collapsing lets somebody who works in two of them hide the
 * other five. See `navigation.ts` for the grouping and why each judgement went the way it did.
 *
 * **The expanded/collapsed state persists, and two things make that safe rather than annoying.** It is
 * stored per group, so collapsing Governance does not also collapse Quality; and a group containing the
 * current page shows a dot on its heading when collapsed, so no amount of collapsing can hide where the
 * reader is. That second property is what stops collapsible navigation being abandoned — without it, a
 * user who collapses everything loses their sense of place and stops using the collapse.
 *
 * **`localStorage` is read once into state rather than on every render**, and every access is guarded:
 * a browser with storage disabled (or a private window in some engines) throws on access rather than
 * returning null, and a navigation sidebar must not be the thing that white-screens the app.
 *
 * The skip link stays first in tab order, and it matters more now than before: a keyboard user should
 * not walk seven group toggles and twenty-seven links to reach a dense table.
 */

function readStored<T>(key: string, fallback: T, parse: (raw: string) => T | null): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    return parse(raw) ?? fallback;
  } catch {
    /* Storage unavailable. The default is a working sidebar, which is the only acceptable outcome. */
    return fallback;
  }
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* A preference that cannot be saved is not an error worth surfacing: the session still works. */
  }
}

/* Groups whose entries only an administrator may reach.
 *
 * **The group is filtered at render time and `NAV_GROUPS` is untouched**, which is what keeps
 * `navigation.test.ts` meaningful: that sweep holds the declared architecture against the router in
 * both directions, and a conditionally-*declared* group would make one of its two directions depend
 * on who is signed in — so an Admin route with no entry would stop being a failure. The data stays
 * the whole architecture; this is a view of it.
 *
 * Both `/admin` and `/admin/connectors` are gated, and the route itself refuses too — see
 * `RequireAdmin` in `App.tsx`. Hiding a link is not access control: a reader who typed the URL would
 * otherwise land on a screen whose every read answers 403, which looks like breakage rather than a
 * boundary.
 */
const ADMIN_ONLY_GROUPS: ReadonlySet<string> = new Set(['admin']);

/* `inert` on a closed disclosure panel, spread rather than passed.
 *
 * **A helper rather than a cast at the call site, because the reason is worth naming once.** React
 * 18's DOM typings predate the `inert` attribute, so `inert={''}` is a type error and `inert={true}`
 * is silently dropped at runtime — the attribute is not in its known set, and a boolean it does not
 * recognise never reaches the DOM. An empty string does, because that is how HTML spells a present
 * boolean attribute. React 19 types it properly and this collapses to `inert={!open}`.
 *
 * Returning `{}` rather than `{ inert: undefined }` for the open case: React omits an `undefined`
 * attribute anyway, but an object whose shape changes is clearer about the intent — the attribute is
 * either there or it is not. */
function inertUnless(open: boolean): { readonly inert?: string } {
  return open ? {} : { inert: '' };
}

export function AppShell(): ReactElement {
  const { t } = useTranslation();
  const location = useLocation();
  /* `isAdmin` gates on `grant:authz.role` — the *write*, not the read. `READER` is derived as one
   * `read` per resource type, so every analyst in the tenant holds `read:authz.role` and gating on
   * it would put the Admin section in front of all of them. See `permissions.ts`, which states the
   * reasoning this shell is relying on rather than repeating it.
   *
   * While the identity is loading `can` denies, so the group appears once rather than appearing and
   * vanishing — a control that flickers away reads as a bug. */
  const identity = useIdentity();
  const groups = isAdmin(identity.data)
    ? NAV_GROUPS
    : NAV_GROUPS.filter((group) => !ADMIN_ONLY_GROUPS.has(group.id));

  const [expanded, setExpanded] = useState<readonly string[]>(() =>
    readStored(NAV_STORAGE_KEY, DEFAULT_EXPANDED, (raw) => {
      const parsed: unknown = JSON.parse(raw);
      if (!Array.isArray(parsed)) return null;
      /* Filtered against the known ids rather than trusted: a stored id from a group that has since
       * been renamed would otherwise sit in state for ever, and a hostile value would render nothing. */
      const ids = new Set(NAV_GROUPS.map((group) => group.id));
      const stored = parsed.filter(
        (id): id is string => typeof id === 'string' && ids.has(id),
      );

      /* **A group the stored state has never heard of starts expanded**, and this is a real defect
       * rather than a nicety. The stored value is the set of *expanded* groups, so a group added
       * after somebody's last visit is absent from it and was therefore collapsed — for every
       * existing user, silently, on the release that introduced it. The Admin group shipped that
       * way and was invisible to anyone who had ever opened the app.
       *
       * The distinction the stored set cannot make is "the user collapsed this" from "this did not
       * exist yet". `seen` records which groups the stored state knew about, so absence from it is
       * unambiguous. A group the user genuinely collapsed is in `seen` and stays collapsed. */
      const seen = new Set(
        readStored(NAV_SEEN_KEY, [] as readonly string[], (rawSeen) => {
          const seenParsed: unknown = JSON.parse(rawSeen);
          return Array.isArray(seenParsed)
            ? seenParsed.filter((id): id is string => typeof id === 'string')
            : null;
        }),
      );
      const unseen = NAV_GROUPS.map((group) => group.id).filter((id) => !seen.has(id));
      /* Deduplicated, and that is a fix rather than tidiness. `[...stored, ...unseen]` appended, so a
       * browser that had visited before `seen` was introduced accumulated every group twice — the
       * real stored value was thirteen entries for seven groups. Harmless to `includes`, and it grows
       * by one group per release for ever, and it makes the stored state unreadable when somebody is
       * trying to work out why a group will not stay open. A `Set` also keeps insertion order, so the
       * user's own sequence survives. */
      return [...new Set([...stored, ...unseen])];
    }),
  );

  const [railed, setRailed] = useState(() =>
    readStored(NAV_COLLAPSED_KEY, false, (raw) => raw === 'true'),
  );

  useEffect(() => {
    writeStored(NAV_STORAGE_KEY, JSON.stringify(expanded));
    /* Every group this build has, expanded or not. Written alongside so the *next* release can tell
     * a group the user collapsed from one that did not exist when they last visited. */
    writeStored(NAV_SEEN_KEY, JSON.stringify(NAV_GROUPS.map((group) => group.id)));
  }, [expanded]);

  useEffect(() => {
    writeStored(NAV_COLLAPSED_KEY, String(railed));
  }, [railed]);

  const toggleGroup = useCallback((id: string) => {
    setExpanded((current) =>
      current.includes(id) ? current.filter((each) => each !== id) : [...current, id],
    );
  }, []);

  return (
    <div className="lq-shell" data-nav={railed ? 'collapsed' : 'expanded'}>
      <a className="lq-skip" href="#lumiq-main">
        {t('shell.skip')}
      </a>

      <header className="lq-shell__header">
        {/* **The brand is a child of the header now**, not its own grid corner — see
            `.lq-shell__brand` for why one continuous bar over one continuous rail beats an L.

            The rail-collapse control stays. The reference does not draw it, and it is the one
            control here that cannot be reached any other way: with it removed, a reader on a
            1280px laptop has no way to reclaim 200px for a wide table. It is styled as the
            quietest thing in the bar rather than dropped, which is the honest trade — see the
            commit note for this deviation being deliberate. */}
        <div className="lq-shell__brand">
          <button
            type="button"
            className="lq-icon-button"
            onClick={() => {
              setRailed(!railed);
            }}
            aria-expanded={!railed}
            aria-label={railed ? t('shell.nav.expand') : t('shell.nav.collapse')}
          >
            <span aria-hidden="true">☰</span>
          </button>
          <span className="lq-brand">
            <span className="lq-brand__name">{t('shell.brand')}</span>
            {/* Not `aria-hidden`: "AI" is part of the product's name, so a screen reader should
                read "Lumiq AI". The previous mark was a decorative graphic and was correctly
                hidden. */}
            <span className="lq-brand__mark">{t('shell.brandMark')}</span>
          </span>
        </div>

        {/* Search is a real form that navigates to the catalogue's own search, which is the one
            surface that already has a server-side search parameter. It is deliberately not a
            cross-entity command palette: the brief asks for global search over datasets, rules,
            incidents, contracts and policies, and no endpoint answers across them — building a
            palette that searched one of the five and silently missed the rest would be the fake
            behaviour the brief rules out. Recorded as the next step rather than faked. */}
        <label className="lq-search">
          {/* A real magnifier rather than the `⌕` character. `U+2315` renders inconsistently across
              faces — in Plex Sans it is a thin ring with a stub that reads as a lowercase Greek
              letter at 14px — and the reference draws it as an SVG for the same reason the nav icons
              are SVG: the glyph has to be the same shape on every machine. */}
          <svg
            className="lq-search__icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
            focusable="false"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            className="lq-search__input"
            type="search"
            placeholder={t('shell.search.placeholder')}
            aria-label={t('shell.search.label')}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              const term = event.currentTarget.value.trim();
              if (term === '') return;
              /* Navigating rather than fetching here: the catalogue screen owns the query, the
                 loading state and the empty state, and duplicating them in the shell would be a
                 second answer to "what does a dataset search look like". */
              window.location.assign(
                `${SCREEN_PATHS.datasets}?q=${encodeURIComponent(term)}`,
              );
            }}
          />
        </label>

        <div className="lq-shell__header-spacer" />
        {/* **First of the header's controls, which is where the reference draws it.** It is the one
            control in the bar that explains the rest, so it sits before the notifications and the
            theme toggle rather than after the identity chip — a reader looking for help reads
            left to right and stops at the first thing that offers it. */}
        <GuideLauncher />
        {/* **Before the theme toggle, and it is the only control here that carries state about the
            reader's work.** A rule waiting for this person's signature was previously discoverable
            only by opening that rule's own page, so the platform asked for two signatures and gave
            nobody a queue to work. The badge is what makes the ask visible; see the component for why
            the feed is derived from three existing reads rather than from an endpoint that does not
            exist. */}
        <NotificationBell />
        <ThemeToggle />
        {/* A rule between the deployment's controls and the reader's own identity, which is what
            the reference draws there. The two halves answer different questions — "what does this
            screen do" against "who am I signed in as" — and without the rule the avatar reads as a
            fourth button. */}
        <span className="lq-shell__divider" aria-hidden="true" />
        {/* The chip is an identity readout rather than an action, so it sits before the one control
            that acts on the identity. */}
        <UserChip />
        <LogoutButton />
      </header>

      <nav className="lq-sidebar" aria-label={t('shell.nav.label')}>
        {groups.map((group) => {
          const open = expanded.includes(group.id);
          /* `startsWith` rather than equality, so a nested route — `/datasets/:id` — still marks its
             group as the one holding the reader. */
          const holdsCurrent = group.entries.some(
            (entry) =>
              location.pathname === entry.to ||
              location.pathname.startsWith(`${entry.to}/`),
          );
          return (
            <div key={group.id} className="lq-navgroup" data-group={group.id}>
              <button
                type="button"
                className="lq-navgroup__toggle"
                aria-expanded={open}
                onClick={() => {
                  toggleGroup(group.id);
                }}
              >
                <span className="lq-navgroup__label">{t(group.labelKey)}</span>
                {/* One chevron, rotated by CSS off `aria-expanded` rather than swapped for a second
                    path — so the glyph cannot disagree with the state a screen reader is told. */}
                <svg
                  className="lq-navgroup__caret"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
                {/* Only when collapsed: an open group already shows the active row. */}
                {holdsCurrent && !open ? (
                  <span
                    className="lq-navgroup__here"
                    /* Titled rather than silent: the dot is the only cue, so it needs a name. */
                    title={t('shell.nav.containsCurrent')}
                  />
                ) : null}
              </button>
              {/* **Always rendered, and hidden by CSS rather than by unmounting.** A group whose
                  rows only exist while it is open cannot animate: there is nothing to transition
                  from, and React removes the subtree the moment the height starts changing. The
                  panel's `visibility` is what keeps the collapsed rows out of the tab order and out
                  of the accessibility tree — see `.lq-navgroup__panel`. */}
              <div
                className="lq-navgroup__panel"
                data-open={open}
                /* **`aria-hidden` and `inert` as well as the CSS, and that is not belt-and-braces
                    for its own sake.** `visibility: hidden` does remove the rows from the tab order
                    and the accessibility tree — while the stylesheet is loaded. These two say the
                    same thing in the markup, so a build that shipped the CSS badly would hide the
                    rows rather than leaving twenty-seven invisible links focusable. They are also
                    the only form of it a jsdom test can see, which is what caught the change: the
                    collapse test asserted the link was gone from the DOM, and it no longer is.
                    `inert=''` rather than a boolean, because React 18 does not know the attribute
                    and would drop `inert={true}`. */
                aria-hidden={!open}
                {...inertUnless(open)}
              >
                <ul className="lq-navgroup__items">
                  {group.entries.map((entry) => (
                    <li key={entry.to}>
                      <NavLink
                        className="lq-nav__link"
                        to={entry.to}
                        /* `end` only for the root, whose path is a prefix of every other route — so
                           without it Home would read as current everywhere. Every other entry wants
                           the prefix match, since `/datasets/:id` should keep Datasets marked. */
                        end={entry.to === '/'}
                        /* The label as a tooltip, always — it costs nothing when the label is
                           visible and it is the only thing identifying a glyph on the collapsed
                           rail, where the label is hidden. Set unconditionally rather than on the
                           railed state so the two cannot drift. */
                        title={t(entry.labelKey)}
                      >
                        {navIcon(entry.to)}
                        <span className="lq-nav__label">{t(entry.labelKey)}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </nav>

      <main className="lq-main" id="lumiq-main">
        <div className="lq-content">
          {/* **The degraded banner is no longer here.** It rendered above the content of *every*
              page, and the reference's screens have no such band — for a good reason beyond
              matching: a warning that is always present on every screen is a warning nobody reads,
              and this one is permanently true on a dev profile, so it trained a reader to skip the
              top of the page. The same information is on `/platform`, which is where a reader goes
              to ask about it, and the nav's own Deployment entry leads there. */}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
